import $ from 'jquery'

import { useDrawer } from '@app/shared/pinia/drawer'
import getPiniaInstance from '@app/shared/pinia/instance'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { isFeatureSupported } from '@app/adapters/silverfin'

export default class LiveInputsFileCollectionInput {
  constructor(liveInputs, locked) {
    this.liveInputs = liveInputs
    this.locked = locked
    this.clickEventHandler = isFeatureSupported(
      'supports_file_attachments_rework'
    )
      ? this.openFileAttachmentsDrawer.bind(this)
      : this.openDocumentSelect.bind(this)

    this.initializeListeners()
  }

  inputFilter() {
    return "[data-behaviour~='live-inputs-file-collection-input']"
  }

  // for a new file added via direct upload
  attributesForDirectUpload(data) {
    return {
      document_direct_upload_path: data.link,
    }
  }

  // for a new file added from permanent files
  attributesForPermanentDocument(data) {
    return {
      copy_from_permanent_document_id: data.permanent_document_id,
    }
  }

  attributesForExport(data) {
    return {
      copy_from_export_id: data.export_id,
      document_user_file_name: `${data.name}.pdf`,
    }
  }

  // for an existing file
  attributesForDocument(data) {
    return {
      document_id: data.document_id,
    }
  }

  paramsForDocumentsDownload(documentSpan) {
    return {
      firm_id: Silverfin.currentFirmId,
      company_id: Silverfin.currentCompanyId,
      name: documentSpan.dataset.name,
      object_id: documentSpan.dataset.objectId,
      object_type: documentSpan.dataset.objectType,
      object_ledger_id: documentSpan.dataset.objectLedgerId,
    }
  }

  initializeListeners() {
    this.liveInputs.on('click', this.inputFilter(), this.clickEventHandler)
  }

  getDocumentAttributes(documentData) {
    const documentAttributes = {
      DirectUpload: this.attributesForDirectUpload(documentData),
      PermanentDocument: this.attributesForPermanentDocument(documentData),
      Document: this.attributesForDocument(documentData),
      Export: this.attributesForExport(documentData),
    }
    return documentAttributes[documentData.linkType]
  }

  openFileAttachmentsDrawer(event) {
    const $documentSpan = $(event.currentTarget)

    const drawerStore = useDrawer(getPiniaInstance())
    drawerStore.openDrawer({
      direction: 'right',
      content: RightDrawerContent.FileAttachments,
      options: {
        textPropertyName: $documentSpan[0].dataset.name,
        updateAttachments: this.updateAttachments.bind(this, $documentSpan),
      },
    })

    // Reset the potential previously active text_input
    this.liveInputs.setActiveElement(null)
  }

  openDocumentSelect(event) {
    //Try to remove this when live-inputs & documentSelect is no longer Jquery dependent
    const $documentSpan = $(event.currentTarget)

    window.modalCitizen.openDocumentSelectModal({
      firmId: Silverfin.currentFirmId,
      companyId: Silverfin.currentCompanyId,
      options: {
        type: 'file_collection',
        locked: this.locked,
        fileCollectionValue: $documentSpan.data('value') || [],
        success: this.updateAttachments.bind(this, $documentSpan),
        download_documents: () => {
          window.location.href =
            Silverfin.Routes.download_documents_text_properties_path(
              this.paramsForDocumentsDownload($documentSpan[0])
            )
        },
      },
    })

    // We set the active element to this, mainly to reset the potential previously active text_input
    return this.liveInputs.setActiveElement($documentSpan)
  }

  updateAttachments(
    $documentSpan,
    documents,
    deletedDocumentIds,
    successCallback,
    errorCallback
  ) {
    if (this.locked) return

    const maxSize = $documentSpan[0].dataset.maxSize

    const processedDocuments = documents.map((documentData) => {
      return {
        document_attributes: {
          ...this.getDocumentAttributes(documentData),
          ...(maxSize && { max_size: maxSize }),
        },
      }
    }, this)

    for (const documentId of deletedDocumentIds) {
      processedDocuments.push({
        document_attributes: {
          document_id: documentId,
          delete: '1',
        },
      })
    }

    this.liveInputs.update($documentSpan, {
      value: processedDocuments,
      submit: true,
      successCallback,
      errorCallback,
    })
  }

  teardown() {
    return this.liveInputs.on(
      'click',
      this.inputFilter(),
      this.clickEventHandler
    )
  }
}
